import type { OfferDraftRowKind, OfferPositionSurchargeConfig, OfferPositionSurchargeMode } from '../../../types';
import { formatEuro } from './offerDocumentUtils';
import { assignHierarchicalNumberLabels } from './offerTitleHierarchy';

export type PositionSurchargeTableRow = {
  id: string;
  kind: OfferDraftRowKind;
  quantity: number;
  unit_price_vk: number;
  item_name?: string;
  numberLabel?: string;
  positionSurcharge?: OfferPositionSurchargeConfig | null;
  isEventual?: boolean;
  titleLevel?: number | null;
};

export type SurchargeTargetCandidate = {
  id: string;
  numberLabel: string;
  item_name: string;
  lineTotal: number;
};

function lineTotal(qty: number, unit: number): number {
  return Math.round(qty * unit * 100) / 100;
}

export function isSurchargeTargetRow(kind: OfferDraftRowKind, isEventual?: boolean | null): boolean {
  if (isEventual) return false;
  return kind === 'apiPosition' || kind === 'freePosition';
}

export function listEligibleSurchargeTargets(
  rows: PositionSurchargeTableRow[],
  beforeIndex: number,
): SurchargeTargetCandidate[] {
  return rows.slice(0, beforeIndex).flatMap((r) => {
    if (!isSurchargeTargetRow(r.kind, r.isEventual)) return [];
    const numberLabel = (r.numberLabel ?? '').trim();
    if (!numberLabel) return [];
    return [
      {
        id: r.id,
        numberLabel,
        item_name: (r.item_name ?? '').trim() || '—',
        lineTotal: lineTotal(r.quantity, r.unit_price_vk),
      },
    ];
  });
}

export function computeSurchargeBaseSum(
  rows: PositionSurchargeTableRow[],
  config: OfferPositionSurchargeConfig,
  rowIndex: number,
): number {
  const targetSet = new Set(config.targetRowIds);
  let sum = 0;
  for (let i = 0; i < rowIndex; i += 1) {
    const r = rows[i];
    if (!r || !targetSet.has(r.id) || !isSurchargeTargetRow(r.kind, r.isEventual)) continue;
    sum += lineTotal(r.quantity, r.unit_price_vk);
  }
  return Math.round(sum * 100) / 100;
}

export function surchargeMode(config: OfferPositionSurchargeConfig): OfferPositionSurchargeMode {
  return config.mode === 'amount' ? 'amount' : 'percent';
}

export function computeSurchargeUnitPrice(baseSum: number, percent: number): number {
  if (!Number.isFinite(baseSum) || !Number.isFinite(percent) || percent <= 0) return 0;
  return Math.round(baseSum * (percent / 100) * 100) / 100;
}

export function computeSurchargeLinePrice(baseSum: number, config: OfferPositionSurchargeConfig): number {
  if (surchargeMode(config) === 'amount') {
    const amount = Number(config.amount);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    return Math.round(amount * 100) / 100;
  }
  return computeSurchargeUnitPrice(baseSum, config.percent);
}

function surchargeHeadLabel(config: OfferPositionSurchargeConfig): string {
  if (surchargeMode(config) === 'amount') {
    const amount = Number.isFinite(config.amount) ? Number(config.amount) : 0;
    return `${formatEuro(amount)} Zuschlag`;
  }
  const pct = Number.isFinite(config.percent) ? config.percent : 0;
  return `${pct.toLocaleString('de-DE')} % Zuschlag`;
}

export function buildSurchargeDescription(percent: number, numberLabels: string[]): string {
  return buildSurchargeDescriptionFromConfig({ percent, targetRowIds: [] }, numberLabels);
}

export function buildSurchargeDescriptionFromConfig(
  config: OfferPositionSurchargeConfig,
  numberLabels: string[],
): string {
  const head = surchargeHeadLabel(config);
  const labels = numberLabels.map((l) => l.trim()).filter(Boolean);
  if (!labels.length) return head;
  const posList = labels.map((l) => `Pos. ${l}`).join(', ');
  return `${head} auf ${posList}`;
}

/** Document-order position labels for a surcharge config (not selection order). */
export function buildSurchargeDescriptionForConfig(
  labeledRows: Array<{ id: string; numberLabel?: string }>,
  config: OfferPositionSurchargeConfig,
): string {
  const targetIdSet = new Set(config.targetRowIds);
  const numberLabels = labeledRows
    .filter((r) => targetIdSet.has(r.id) && r.numberLabel)
    .map((r) => r.numberLabel as string);
  return buildSurchargeDescriptionFromConfig(config, numberLabels);
}

export function surchargeConfigTargetsOrPercentChanged(
  prev: OfferPositionSurchargeConfig | null | undefined,
  next: OfferPositionSurchargeConfig,
): boolean {
  if (!prev) return true;
  if (surchargeMode(prev) !== surchargeMode(next)) return true;
  if (surchargeMode(next) === 'amount') {
    if ((prev.amount ?? 0) !== (next.amount ?? 0)) return true;
  } else if (prev.percent !== next.percent) {
    return true;
  }
  const prevSet = new Set(prev.targetRowIds);
  if (prev.targetRowIds.length !== next.targetRowIds.length) return true;
  return next.targetRowIds.some((id) => !prevSet.has(id));
}

type SurchargeTargetLookupRow = Pick<PositionSurchargeTableRow, 'id' | 'kind' | 'isEventual'>;

/** Target row ids that still contribute to the surcharge at the given row index. */
export function getEffectiveSurchargeTargetIds(
  rows: SurchargeTargetLookupRow[],
  config: OfferPositionSurchargeConfig,
  rowIndex: number,
): string[] {
  const targetSet = new Set(config.targetRowIds);
  const effective: string[] = [];
  for (let i = 0; i < rowIndex; i += 1) {
    const r = rows[i];
    if (!r || !targetSet.has(r.id) || !isSurchargeTargetRow(r.kind, r.isEventual)) continue;
    effective.push(r.id);
  }
  return effective;
}

export function buildSurchargeDescriptionForEffectiveTargets(
  labeledRows: Array<SurchargeTargetLookupRow & { numberLabel?: string }>,
  config: OfferPositionSurchargeConfig,
  rowIndex: number,
): string {
  const effectiveIds = new Set(getEffectiveSurchargeTargetIds(labeledRows, config, rowIndex));
  const numberLabels = labeledRows
    .filter((r) => effectiveIds.has(r.id) && r.numberLabel)
    .map((r) => r.numberLabel as string);
  return buildSurchargeDescriptionFromConfig(config, numberLabels);
}

export function reconcileSurchargeDraftRows(
  draftRows: Array<{
    id: string;
    kind: OfferDraftRowKind;
    positionSurcharge?: OfferPositionSurchargeConfig | null;
    unit_price_vk: number;
    description: string;
  }>,
  orderedRows: PositionSurchargeTableRow[],
): typeof draftRows {
  const labeled = assignPositionNumberLabels(orderedRows);
  const visibleIds = new Set(labeled.map((r) => r.id));

  return draftRows.map((draft) => {
    if (draft.kind !== 'positionSurcharge' || !draft.positionSurcharge) return draft;
    const rowIndex = labeled.findIndex((r) => r.id === draft.id);
    if (rowIndex < 0) return draft;

    const targetRowIds = draft.positionSurcharge.targetRowIds.filter((id) => visibleIds.has(id));
    const config: OfferPositionSurchargeConfig = { ...draft.positionSurcharge, targetRowIds };
    const unit_price_vk = computeSurchargeLinePrice(
      computeSurchargeBaseSum(labeled, config, rowIndex),
      config,
    );
    const description = buildSurchargeDescriptionForEffectiveTargets(labeled, config, rowIndex);

    const targetsUnchanged =
      draft.positionSurcharge.targetRowIds.length === targetRowIds.length &&
      draft.positionSurcharge.targetRowIds.every((id, i) => id === targetRowIds[i]);
    if (
      targetsUnchanged &&
      !surchargeConfigTargetsOrPercentChanged(draft.positionSurcharge, config) &&
      draft.unit_price_vk === unit_price_vk &&
      draft.description === description
    ) {
      return draft;
    }

    return { ...draft, positionSurcharge: config, unit_price_vk, description };
  });
}

export function applyPositionSurchargePricing<T extends PositionSurchargeTableRow>(rows: T[]): T[] {
  return rows.map((row, rowIndex) => {
    if (row.kind !== 'positionSurcharge' || !row.positionSurcharge) return row;
    const baseSum = computeSurchargeBaseSum(rows, row.positionSurcharge, rowIndex);
    const unit_price_vk = computeSurchargeLinePrice(baseSum, row.positionSurcharge);
    if (unit_price_vk === row.unit_price_vk) return row;
    return { ...row, unit_price_vk };
  });
}

export function assignPositionNumberLabels<T extends PositionSurchargeTableRow>(
  rows: T[],
): Array<T & { numberLabel: string; rowDepth: number; titleLevel: number }> {
  return assignHierarchicalNumberLabels(rows);
}
